import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import AssignSourceOfFund from "./AssignSourceOfFund";
import AddNewSourceOfFund from "./AddNewSourceOfFund"; 

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.8, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.015),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflow: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

function Sourceoffunds() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  // NEW: Search and Modal states
  const [searchTerm, setSearchTerm] = useState("");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const sourcesPerPage = 10;
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/support/source-of-funds");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSources(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching source of funds:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  // NEW: Filtering logic based on searchTerm
  const filteredSources = sources.filter((item) => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;
    return (
      item.id?.toString().toLowerCase().includes(search) ||
      item.name?.toLowerCase().includes(search)
    );
  });
  const sortedSources = [...filteredSources].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const indexOfLastSource = currentPage * sourcesPerPage;
  const indexOfFirstSource = indexOfLastSource - sourcesPerPage;
  const currentSources = sortedSources.slice(
    indexOfFirstSource,
    indexOfLastSource,
  );

  const handleMenuOpen = (event, source) => {
    setAnchorEl(event.currentTarget);
    setSelectedSource(source);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddNew = () => {
    setAddModalOpen(true);
  };

  const handleEdit = () => {
    console.log("Edit source:", selectedSource);
    handleMenuClose();
  };

  const handleAssign = () => {
    setIsAssignModalOpen(true);
    setAnchorEl(null);
  };

  const handleAssignModalClose = () => {
    setIsAssignModalOpen(false);
    setSelectedSource(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 3,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        {/* TITLE */}
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: PRIMARY_COLOR }}
          >
            Source of Funds
          </Typography>
        </Box>

        {/* SEARCH + ADD NEW BUTTON */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search source of funds..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              minHeight: 40,
              whiteSpace: "nowrap",
              px: 2,
            }}
          >
            Add New
          </Button>
        </Box>
      </Box>

      {/* RESULT COUNT */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing <strong>{filteredSources.length}</strong>{" "}
          {filteredSources.length === 1 ? "source" : "sources"}
        </Typography>
      </Box>

      <ResponsiveTableContainer component={Paper} elevation={0}>
        <Table stickyHeader size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: theme.palette.primary.light,
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  borderBottom: "none",
                  py: 1.8,
                },
              }}
            >
              {[
                { id: "id", label: "ID" },
                { id: "name", label: "Name" },
                { id: "isActive", label: "Status" },
                { id: "actions", label: "Actions" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => {
                    if (column.id !== "actions") {
                      handleSort(column.id);
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {column.label}
                    {sortConfig.key === column.id &&
                      column.id !== "actions" && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{ lineHeight: 0.5 }}
                        >
                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity: sortConfig.direction === "asc" ? 1 : 0.4,
                            }}
                          >
                            ▲
                          </span>
                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity:
                                sortConfig.direction === "desc" ? 1 : 0.4,
                            }}
                          >
                            ▼
                          </span>
                        </Box>
                      )}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    Loading source of funds...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentSources.length > 0 ? (
              currentSources.map((source) => (
                <StyledTableRow key={source.id} hover>
                  <ModernTableCell>
                    <Chip
                      label={`#${source.id}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                        borderRadius: "6px",
                      }}
                    />
                  </ModernTableCell>
                  <ModernTableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                    >
                      {source.name}
                    </Typography>
                  </ModernTableCell>
                  <ModernTableCell>
                    <Chip
                      label={source.isActive ? "Active" : "Inactive"}
                      color={source.isActive ? "success" : "default"}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </ModernTableCell>
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, source)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: PRIMARY_COLOR,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                          },
                        }}
                      >
                        <MoreVertIcon
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={4} align="center" sx={{ py: 7 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    No source of funds found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Try changing your search criteria.
                  </Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      {filteredSources.length > sourcesPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredSources.length / sourcesPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  bgcolor: PRIMARY_COLOR,
                  "&:hover": {
                    bgcolor: SECONDARY_COLOR,
                  },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,
            "&:hover": { backgroundColor: alpha(PRIMARY_COLOR, 0.05) },
          }}
        >
          <EditIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Edit</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleAssign}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,
            "&:hover": { backgroundColor: alpha(SECONDARY_COLOR, 0.06) },
          }}
        >
          <AssignmentIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
          <Typography variant="body2">Assign</Typography>
        </MenuItem>
      </Menu>

      {/* Modals */}
      {isAssignModalOpen && (
        <AssignSourceOfFund
          open={isAssignModalOpen}
          onClose={handleAssignModalClose}
          sourceOfFund={selectedSource}
          onSaveSuccess={() => {
            fetchSources();
          }}
        />
      )}

      {/* Add New Modal Placeholder */}
      
        <AddNewSourceOfFund
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false);
            fetchSources();
          }}
        />
     
    </Box>
  );
}

export default Sourceoffunds;
